import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    settings: {},                 // full settings object from backend
    globalReturnMultiplier: null,
    timer: 0,
    timerDisplay: "00:00",
    phase: "bidding", // bidding, autosubmit, hold
    roundNumber: 0,
    status: "running",
    bidPredictions: {}, // { "0": 5, "1": 12, ... }
    bidDetails: {}, // { "0": { count: 5, totalAmount: 2500 }, "1": { count: 12, totalAmount: 6000 } }
    totalAmount: 0,
    selectedWinner: null,
    isManualWinnerWindowOpen: false,
    loading: false,
    error: null,
};

const settingsSlice = createSlice({
    name: "settings",
    initialState,
    reducers: {
        fetchSettingsStart(state) {
            state.loading = true;
            state.error = null;
        },
        fetchSettingsSuccess(state, action) {
            state.loading = false;
            state.error = null;
            state.settings = action.payload;
            state.globalReturnMultiplier = action.payload.globalReturnMultiplier;
        },
        fetchSettingsFailure(state, action) {
            state.loading = false;
            state.error = action.payload;
        },
        updateTimer(state, action) {
            const timeLeft = action.payload;
            state.timer = timeLeft;
            // Format timer as MM:SS
            const minutes = Math.floor(timeLeft / 60);
            const seconds = timeLeft % 60;
            state.timerDisplay = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
        },
        updateGameState(state, action) {
            const { timeLeft, phase, roundNumber, status } = action.payload;

            // Update timer
            if (timeLeft !== undefined) {
                state.timer = timeLeft;
                const minutes = Math.floor(timeLeft / 60);
                const seconds = timeLeft % 60;
                state.timerDisplay = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
            }

            // Update phase
            if (phase) {
                state.phase = phase;
            }

            // Update round number
            if (roundNumber !== undefined) {
                state.roundNumber = roundNumber;
            }

            // Update status
            if (status) {
                state.status = status;
            }

            // Manual winner window is open during autosubmit phase (10s-3s)
            if (phase === "autosubmit" || (timeLeft <= 10 && timeLeft > 3 && phase === "bidding")) {
                state.isManualWinnerWindowOpen = true;
            } else if (timeLeft <= 3) {
                state.isManualWinnerWindowOpen = false;
            }
        },
        updateBidPredictions(state, action) {
            state.bidPredictions = action.payload.bids || {};
            state.totalAmount = action.payload.totalAmount || 0;
        },

        // NEW: Handle placeBid event
        handlePlaceBid(state, action) {
            const { number, amount } = action.payload;

            if (number === undefined || amount === undefined) {
                console.error('Invalid placeBid data:', action.payload);
                return;
            }

            const numStr = String(number);

            // Initialize bidDetails for this number if it doesn't exist
            if (!state.bidDetails[numStr]) {
                state.bidDetails[numStr] = {
                    count: 0,
                    totalAmount: 0
                };
            }

            // Update count and total amount for this number
            state.bidDetails[numStr] = {
                count: (state.bidDetails[numStr].count || 0) + 1,
                totalAmount: (state.bidDetails[numStr].totalAmount || 0) + amount
            };

            // Also update bidPredictions for backward compatibility
            state.bidPredictions[numStr] = (state.bidPredictions[numStr] || 0) + 1;

            // Update total amount
            state.totalAmount = (state.totalAmount || 0) + amount;

            console.log(`Bid placed on number ${number}:`, {
                amount,
                newCount: state.bidDetails[numStr].count,
                newTotalAmount: state.bidDetails[numStr].totalAmount,
                totalRoundAmount: state.totalAmount
            });
        },

        // NEW: Update bid details from initial data
        updateBidDetails(state, action) {
            const { number, details } = action.payload;

            if (number !== undefined && details) {
                const numStr = String(number);
                state.bidDetails[numStr] = {
                    count: details.count || 0,
                    totalAmount: details.totalAmount || 0
                };
            } else if (typeof action.payload === 'object') {
                // If payload is an object with all bid details
                state.bidDetails = { ...action.payload };
            }
        },

        // NEW: Reset bid details on new round
        resetBidDetails(state) {
            state.bidDetails = {};
        },

        setManualWinnerWindow(state, action) {
            state.isManualWinnerWindowOpen = action.payload;
        },
        setSelectedWinner(state, action) {
            state.selectedWinner = action.payload;
        },
        resetRound(state) {
            state.bidPredictions = {};
            state.bidDetails = {}; // Also reset bid details
            state.totalAmount = 0;
            state.selectedWinner = null;
            state.isManualWinnerWindowOpen = false;
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
            state.loading = false;
        },
    },
});

export const {
    fetchSettingsStart,
    fetchSettingsSuccess,
    fetchSettingsFailure,
    updateTimer,
    updateGameState,
    updateBidPredictions,
    handlePlaceBid, // NEW: Export handlePlaceBid
    updateBidDetails, // NEW: Export updateBidDetails
    resetBidDetails, // NEW: Export resetBidDetails
    setManualWinnerWindow,
    setSelectedWinner,
    resetRound,
    setLoading,
    setError,
} = settingsSlice.actions;

export default settingsSlice.reducer;